import { html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { BaseElement } from "../utils/ui-components";
import { MediaItem, MediaItemSegment } from "../data/types";
import { SearchResultMatch } from "../server/media";
import { aiIcon, playCircleIcon } from "../utils/icons";
import { AudioState } from "./audio-player";
import { ChatContextItem } from "../api";

@customElement("segment-view")
export class SegmentView extends BaseElement {
    @property()
    item!: MediaItem;

    @property()
    segment!: MediaItemSegment;

    @property()
    segmentMatches: (SearchResultMatch & { type: "segment" | "subtitle" })[] = [];

    @property()
    onAudioRequest?: (state: AudioState) => void;

    private addToContext = () => {
        const event = new CustomEvent<ChatContextItem>("add-to-context", {
            detail: {
                type: "segment",
                item: this.item,
                segment: this.segment,
            },
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(event);
    };

    render() {
        const segmentMatch = this.segmentMatches.find((m) => m.type == "segment" && m.segmentIndex == this.segment.index);
        const subtitleMatches = this.segmentMatches.filter((m) => m.type == "subtitle" && m.segmentIndex == this.segment.index);

        return html`
            <div class="mt-4 first:mt-2 bg-gray-50 rounded-lg p-4">
                <!-- Header row -->
                <div class="flex items-start gap-3">
                    <div class="flex-1 min-w-0">
                        <div class="font-medium text-gray-800 text-sm break-words [overflow-wrap:anywhere]">
                            ${unsafeHTML((segmentMatch?.highlights as { title?: string; text?: string })?.title || this.segment.title)}
                        </div>
                    </div>
                    <div class="flex gap-2">
                        <button
                            @click=${this.addToContext}
                            class="p-1.5 rounded-full hover:bg-white/80 text-blue-600 hover:text-blue-700 transition-colors shrink-0"
                            title="Add to chat context"
                        >
                            <i class="w-5 h-5 icon">${aiIcon}</i>
                        </button>
                        <button
                            @click=${() => this.onAudioRequest?.({ item: this.item, segment: this.segment })}
                            class="p-1.5 rounded-full hover:bg-white/80 text-blue-600 hover:text-blue-700 transition-colors shrink-0"
                        >
                            <i class="w-6 h-6 icon">${playCircleIcon}</i>
                        </button>
                    </div>
                </div>

                <!-- Segment text row -->
                <div
                    class="text-sm text-gray-600 whitespace-pre-line break-words [overflow-wrap:anywhere] mt-1"
                    .innerHTML=${segmentMatch?.highlights.text || this.segment.text}
                ></div>

                ${this.segment.subtitles?.length
                    ? html`
                          <subtitle-list-view
                              .subtitles=${this.segment.subtitles}
                              .subtitleMatches=${subtitleMatches}
                              .item=${this.item}
                              .segment=${this.segment}
                              .onAudioRequest=${this.onAudioRequest}
                          ></subtitle-list-view>
                      `
                    : ""}
            </div>
        `;
    }
}
